/* eslint-disable no-underscore-dangle */
// @flow
import React from 'react';
import type { Dispatch } from 'redux';
import { createGlobalStyle, ThemeProvider } from 'styled-components';
import { withRouter, matchPath } from 'react-router-dom';
import { OmniNav } from '@finect/tabular-omninav';
import { finect as theme } from '@finect/tabular-themes/finect';
import { reset } from '@finect/tabular-utils/reset';
import { isPreRelease } from '@finect/front-utils/isPreRelease';
import { Main } from '@finect/tabular-core/Main';
import { Content } from '@finect/tabular-core/Content';
import CookiesCMP from '@finect/front-core/CookiesCMP';
import { Footer } from '@finect/tabular-components/Footer';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { getLoggedUser, fetchUserNotifications } from '@finect/front-core/User';
import GoogleSignIn from '@finect/front-core/GoogleSignIn';
import { ScrollAnchor } from '@finect/front-core/ScrollAnchor';
import { routes } from './scenes/routes';
import { LoginModal, PlusLimitModal } from './components';
import { AppContextual } from './components/AppContextual';
import { sendLogError } from './utils';

const PAYMENT_SUBSCRIPTION_URL = '/servicios/finect-subscription';
const PAYMENT_PLUS_URL = '/servicios/finect-plus';

/* eslint-disable */
const GlobalStyles = createGlobalStyle`
  ${reset};
`;
/* eslint-enable */

export function fetchAppInitialData(cookie: string = 'include') {
  return function (dispatch: Dispatch, getState: Function) {
    const promises = [];
    if (!getState().user.user.hasOwnProperty('isLoggedIn')) promises.push(dispatch(getLoggedUser(cookie)));

    return Promise.all(promises);
  };
}

const mapStateToProps = state => ({
  user: state.user.user
});

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators(
    {
      fetchUserNotifications,
      fetchAppInitialData
    },
    dispatch
  )
});

type Props = {
  user: Object,
  actions: Object,
  children: any,
  omniNavData?: Object,
  location?: Object
};

class App extends React.Component<Props> {
  componentDidMount() {
    this.props.actions.fetchAppInitialData();

    // START ------------------------------------------- INP measurement using PerformanceObserver
    if (typeof window !== 'undefined' && 'PerformanceObserver' in window) {
      try {
        const INP_THRESHOLD = 200;

        const observer = new window.PerformanceObserver((list) => {
          list.getEntries().forEach((entry) => {
            // INP entries have entryType 'event' and name 'INP' or 'interaction'
            // Modern browsers use 'event' with interactionId and duration
            // We'll just check for 'interaction' type if supported, or INP polyfill
            if (
              (entry.name === 'INP' ||
                entry.entryType === 'event' ||
                entry.entryType === 'longtask' ||
                entry.interactionId) &&
              !entry.name.startsWith('mouse') &&
              !entry.name.startsWith('pointer') &&
              !entry.name.startsWith('key')
            ) {
              const inpValue = entry.duration || entry.processingEnd - entry.startTime;
              if (inpValue > INP_THRESHOLD) {
                const { target } = entry;
                const { id, className, nodeName, parentElement } = target || {};
                const parentClassName = parentElement?.className || '';

                // prettier-ignore
                const log = `[INP] Interaction ${entry.name} exceeded threshold: ${inpValue.toFixed(2)} ms`;
                const targetInfo = ` - name:${nodeName} - id:${id} - class:${className} ${className?.baseVal}  - parentClass:${parentClassName}`;
                sendLogError(200, log + (target ? targetInfo : ''));
              }
            }
          });
        });
        // See if the browser supports 'event' or 'longtask' or 'interaction'
        if (PerformanceObserver.supportedEntryTypes?.includes('event')) {
          observer.observe({ type: 'event', buffered: true });
        } else if (PerformanceObserver.supportedEntryTypes?.includes('longtask')) {
          observer.observe({ type: 'longtask', buffered: true });
        }
        // Optionally, disconnect on unmount if needed
        this._inpObserver = observer;
      } catch (e) {
        // eslint-disable-next-line no-console
        isPreRelease && console.warn('[INP] PerformanceObserver setup failed:', e);
      }
    }
  }

  componentWillUnmount() {
    // Clean up PerformanceObserver if needed
    if (this._inpObserver && typeof this._inpObserver.disconnect === 'function') {
      this._inpObserver.disconnect();
    }
  }
  // END ------------------------------------------- INP measurement using PerformanceObserver

  static loadInitialData(store, cookie) {
    return store.dispatch(fetchAppInitialData(cookie));
  }

  newsletterAction = (preferences: Object) => {
    this.props.actions.fetchUserNotifications(preferences);
  };

  render() {
    if (!this.props.user.hasOwnProperty('isLoggedIn')) return null;
    const { user, children, omniNavData, location } = this.props;
    const pathname = location ? location.pathname : null;

    const omninavShadowMatches = routes
      .filter(({ omninavShadow = false }) => omninavShadow)
      .filter(route => matchPath(pathname, route));

    const showCookiesCMP =
      pathname && !pathname.startsWith(PAYMENT_SUBSCRIPTION_URL) && !pathname.startsWith(PAYMENT_PLUS_URL);

    // Redirect to Plus purchase page if user is not Plus and tries to access Plus content
    if (!user.plus && pathname?.startsWith('/plus/')) {
      if (typeof window !== 'undefined') window.location.assign(`${PAYMENT_PLUS_URL}/comprar`);
      return null;
    }

    // Redirect to Plus selection page if user is Plus and tries to access Plus purchase page
    if (user.plus && pathname?.startsWith(PAYMENT_PLUS_URL) && typeof window !== 'undefined') {
      if (typeof window !== 'undefined') window.location.assign('/plus/seleccion');
      return null;
    }

    return (
      <ThemeProvider theme={theme}>
        {showCookiesCMP && <CookiesCMP user={user} />}

        <GoogleSignIn />
        <ScrollAnchor location={this.props.location} />
        <AppContextual />
        <Main>
          <GlobalStyles />

          <OmniNav
            user={this.props.user}
            finectApp="front-products"
            omniNavData={omniNavData}
            withShadow={Boolean(omninavShadowMatches.length)}
          />

          <LoginModal />
          <PlusLimitModal url={`${PAYMENT_PLUS_URL}/comprar`} />

          <Content fullHeight>{children}</Content>
          <Footer user={user} withConversations newsletterAction={this.newsletterAction} />
        </Main>
      </ThemeProvider>
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(App);
export const AppWithRouter = withRouter(connect(mapStateToProps, mapDispatchToProps)(App));
